import { Link as GatsbyLink } from "gatsby";
import React from "react";
import AnchorLink from "@bmi-digital/components/anchor-link";
import { useIsClient } from "@bmi-digital/components/hooks";
import { transformHyphens } from "@bmi-digital/components";
import { constructUrlWithPrevPage } from "../utils/myAccountUtils";
import { getPathWithCountryCode } from "../utils/path";
import type {
  RichTextAssetLinkBlock,
  RichTextExternalLinkBlock,
  RichTextHubSpotLinkBlock,
  RichTextInternalLinkBlock,
  RichTextLinkAssetLinkBlock,
  RichTextLinkExternalLinkBlock,
  RichTextLinkHubSpotLinkBlock,
  RichTextLinkInternalLinkBlock
} from "./link/types";

interface Props {
  data: [
    | RichTextInternalLinkBlock
    | RichTextLinkInternalLinkBlock
    | RichTextExternalLinkBlock
    | RichTextLinkExternalLinkBlock
    | RichTextAssetLinkBlock
    | RichTextLinkAssetLinkBlock
    | RichTextHubSpotLinkBlock
    | RichTextLinkHubSpotLinkBlock
  ];
  label: string;
  gtmLabel?: React.ReactNode;
  hyperlinkColor?: "default" | "black" | "white";
  className?: string;
  "data-testid"?: string;
}

const getHref = (data: Props["data"], isClient: boolean): string => {
  const { blockType } = data[0];
  if (
    blockType === "rich-text-internal-link-block" ||
    blockType === "rich-text-link-internal-link-block"
  ) {
    const { linkedPage } = data[0];
    return getPathWithCountryCode(
      process.env.GATSBY_SPACE_MARKET_CODE,
      linkedPage.value.path
    );
  }

  if (
    blockType === "rich-text-external-link-block" ||
    blockType === "rich-text-link-external-link-block"
  ) {
    const { url } = data[0];
    return isClient &&
      process.env.GATSBY_INTOUCH_ORIGIN &&
      url.includes(process.env.GATSBY_INTOUCH_ORIGIN)
      ? constructUrlWithPrevPage(url)
      : url;
  }

  if (
    blockType === "rich-text-asset-link-block" ||
    blockType === "rich-text-link-asset-link-block"
  ) {
    return data[0].asset.url;
  }

  return `${process.env.GATSBY_HUBSPOT_CTA_URL}${process.env.GATSBY_HUBSPOT_ID}/${data[0].hubSpotCTAID}`;
};

const InlineLink = ({
  data,
  label,
  gtmLabel,
  hyperlinkColor,
  className,
  "data-testid": dataTestId
}: Props) => {
  const { isClient } = useIsClient();
  const href = getHref(data, isClient);
  const { blockType } = data[0];
  const isExternalOrAssetLink =
    blockType === "rich-text-external-link-block" ||
    blockType === "rich-text-link-external-link-block" ||
    blockType === "rich-text-asset-link-block" ||
    blockType === "rich-text-link-asset-link-block";

  return (
    <AnchorLink
      component={
        blockType === "rich-text-internal-link-block" ||
        blockType === "rich-text-link-internal-link-block"
          ? GatsbyLink
          : undefined
      }
      external={isExternalOrAssetLink}
      to={
        blockType === "rich-text-internal-link-block" ||
        blockType === "rich-text-link-internal-link-block"
          ? href
          : undefined
      }
      href={
        !(
          blockType === "rich-text-internal-link-block" ||
          blockType === "rich-text-link-internal-link-block"
        )
          ? href
          : undefined
      }
      download={
        blockType === "rich-text-asset-link-block" ||
        blockType === "rich-text-link-asset-link-block"
          ? true
          : undefined
      }
      gtm={{
        id: "cta-click1",
        label: gtmLabel ? `${gtmLabel} - ${label}` : label,
        action: href
      }}
      className={className}
      color={hyperlinkColor}
      data-testid={dataTestId}
    >
      {transformHyphens(label)}
    </AnchorLink>
  );
};

export default InlineLink;
